import moment from 'moment';
import 'moment/locale/pt-br';

export type MeetingType = 'ONLINE' | 'PRESENCIAL';

export type MeetingRecurrenceType = 'DAILY' | 'WEEKLY' | 'MONTHLY';

export type MeetingRecurrence = {
    idMeetingRecurrence: number;
    title?: string | null;
    type: MeetingRecurrenceType;
    interval: number;
    finishDate?: string | null;
    weekDays?: string | number[] | null;
};

export type MeetingItem = {
    idMeeting: number;
    idMeetingRecurrence?: number | null;
    title?: string | null;
    nodeCode?: string;
    meetingType: MeetingType;
    startDate: string;
    finishDate: string;
    address?: string | null;
    speaker?: string | null;
    meetingRecurrence?: MeetingRecurrence | null;
};

const ptBR = (value?: moment.MomentInput) => moment(value).locale('pt-br');

export const HIGHLIGHT_LIMIT = 3;
export const SECTION_LIMIT = 6;

const WEEK_DAY_NAMES = {
    0: 'domingo',
    1: 'segunda-feira',
    2: 'terça-feira',
    3: 'quarta-feira',
    4: 'quinta-feira',
    5: 'sexta-feira',
    6: 'sábado',
};

export const normalizeWeekDays = (weekDays: string | number[] | null | undefined): number[] => {
    const sort = (days: number[]) => days.sort((first, second) => first - second);

    if (Array.isArray(weekDays)) {
        return sort(weekDays.map(Number).filter((day) => !Number.isNaN(day)));
    }

    if (typeof weekDays === 'string') {
        return sort(
            weekDays
                .split(',')
                .map((day) => Number(day.trim()))
                .filter((day) => !Number.isNaN(day))
        );
    }

    return [];
};

export const isRecurrent = (meeting: MeetingItem): boolean =>
    Boolean(meeting?.idMeetingRecurrence && meeting?.meetingRecurrence);

export const getMeetingTitle = (meeting: MeetingItem, fallback = ''): string =>
    meeting?.title || meeting?.meetingRecurrence?.title || fallback;

export const isOnline = (meeting: MeetingItem): boolean => meeting?.meetingType === 'ONLINE';

export const withProtocol = (address: string): string =>
    !address.startsWith('http://') && !address.startsWith('https://') ? `http://${address}` : address;

export const sortByStartDate = (meetings: MeetingItem[]): MeetingItem[] =>
    [...meetings].sort((first, second) => moment(first.startDate).valueOf() - moment(second.startDate).valueOf());

export const isPastMeeting = (meeting: MeetingItem, reference: moment.Moment = moment()): boolean =>
    moment(meeting?.finishDate || meeting?.startDate).isBefore(reference);

export const getUpcomingMeetings = (meetings: MeetingItem[], reference?: moment.Moment): MeetingItem[] =>
    sortByStartDate(meetings).filter((meeting) => !isPastMeeting(meeting, reference));

export const splitByHighlight = (meetings: MeetingItem[], limit = HIGHLIGHT_LIMIT) => ({
    highlighted: meetings.slice(0, limit),
    standard: meetings.slice(limit),
});

const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

export const formatCardDate = (meeting: MeetingItem): string => {
    const start = ptBR(meeting.startDate);
    const weekDay = capitalize(start.format('ddd'));
    const month = capitalize(start.format('MMM'));
    const finish = ptBR(meeting.finishDate).format('HH:mm');

    return `${weekDay}, ${start.format('DD')} ${month} • ${start.format('HH:mm')} às ${finish}`;
};

export const formatModalDate = (meeting: MeetingItem): string =>
    capitalize(ptBR(meeting.startDate).format('dddd, DD [de] MMMM'));

export const formatModalTime = (meeting: MeetingItem): string =>
    `${ptBR(meeting.startDate).format('HH:mm')} às ${ptBR(meeting.finishDate).format('HH:mm')}`;

export const getRecurrenceDescription = (meeting: MeetingItem): string => {
    const config = meeting?.meetingRecurrence;

    if (!config?.finishDate) {
        return '';
    }

    const finishDate = ptBR(config.finishDate).format('DD/MM/YYYY');

    if (config.type === 'DAILY') {
        if (config.interval === 1) {
            return `Todos os dias até ${finishDate}`;
        }

        return `A cada ${config.interval} dias até ${finishDate}`;
    }

    if (config.type === 'MONTHLY') {
        const recurrenceDay = ptBR(meeting.startDate).format('D');

        if (config.interval === 1) {
            return `Dia ${recurrenceDay} de cada mês até ${finishDate}`;
        }

        return `Dia ${recurrenceDay} a cada ${config.interval} meses até ${finishDate}`;
    }

    const selectedDays = normalizeWeekDays(config.weekDays)
        .map((day) => WEEK_DAY_NAMES[day])
        .filter(Boolean)
        .join(', ');
    const weekInterval = config.interval === 1 ? 'a cada semana' : `a cada ${config.interval} semanas`;

    return `Toda ${selectedDays} ${weekInterval} até ${finishDate}`;
};

export type MeetingMonth = {
    year: number;
    month: number;
    count: number;
};

export const monthKey = ({ year, month }: Pick<MeetingMonth, 'year' | 'month'>): string =>
    `${year}-${String(month).padStart(2, '0')}`;

export const formatMonthTitle = ({ year, month }: Pick<MeetingMonth, 'year' | 'month'>): string =>
    capitalize(ptBR({ year, month: month - 1, day: 1 }).format('MMMM YYYY'));

export const formatMonthName = ({ year, month }: Pick<MeetingMonth, 'year' | 'month'>): string =>
    ptBR({ year, month: month - 1, day: 1 }).format('MMMM');

export const fillMonthGaps = (months: MeetingMonth[]): MeetingMonth[] => {
    if (!months.length) {
        return [];
    }

    const sorted = [...months].sort((first, second) =>
        first.year === second.year ? first.month - second.month : first.year - second.year
    );

    const byKey = new Map(sorted.map((item) => [monthKey(item), item]));
    const cursor = moment({ year: sorted[0].year, month: sorted[0].month - 1, day: 1 });
    const last = sorted[sorted.length - 1];
    const end = moment({ year: last.year, month: last.month - 1, day: 1 });
    const filled: MeetingMonth[] = [];

    while (cursor.isSameOrBefore(end, 'month')) {
        const key = monthKey({ year: cursor.year(), month: cursor.month() + 1 });

        filled.push(byKey.get(key) || { year: cursor.year(), month: cursor.month() + 1, count: 0 });
        cursor.add(1, 'month');
    }

    return filled;
};

export const getDefaultOpenMonth = (months: MeetingMonth[], reference: moment.Moment = moment()): string | null => {
    const current = months.find(
        (item) => item.count > 0 && item.year === reference.year() && item.month === reference.month() + 1
    );

    if (current) {
        return monthKey(current);
    }

    const upcoming = months.find(
        (item) => item.count > 0 && moment({ year: item.year, month: item.month - 1, day: 1 }).isAfter(reference, 'month')
    );

    if (upcoming) {
        return monthKey(upcoming);
    }

    const any = months.find((item) => item.count > 0);

    return any ? monthKey(any) : null;
};
